<template>
  <div class="redirectors-tab">
    <v-expansion-panels
      v-if="hasProject && !loading && redirectors.length"
      v-model="redirectorsPanel"
      multiple
      accordion
      class="mt-4 d-flex expansion"
    >
      <v-expansion-panel
        v-for="redirector in redirectors"
        :key="redirector.redirector_id"
        class="expansion-panel"
      >
        <v-expansion-panel-header class="expansion-panel-header" style="min-height: 64px">
          <div class="d-flex align-center justify-space-between ">
            <div class="d-flex align-start">
              <div class="mr-6">
                <div class="caption text-uppercase grey--text">Redirector Domain</div>
                <div class="body-2 font-weight-medium">{{ redirector.name }}</div>
              </div>
              <div>
                <div class="caption text-uppercase grey--text">NS Status</div>
                <v-chip
                  small
                  outlined
                  :color="redirector.status === 'active' ? 'success' : 'grey'"
                  class="pa-2 text-capitalize"
                >
                  {{ redirector.status }}
                </v-chip>
              </div>
            </div>
            <v-btn
              v-if="canEdit"
              small
              outlined
              color="success"
              class="px-2 mr-2"
              @click.stop="$emit('edit-redirector', redirector)"
            >
              Edit redirector
            </v-btn>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="expansion-panel-content">
          <v-divider />
          <div class="d-flex align-center pa-4" style="gap: 24px">
            <div>
              <v-select
                :value="redirector.group_mirror_id"
                :items="groupSelectItems"
                item-text="text"
                item-value="value"
                label="Mirror group"
                dense
                hide-details
                readonly
                style="max-width: 220px"
              />
            </div>
            <div class="d-flex flex-column justify-space-between expansion-panel-header__col">
              <span class="caption grey--text text-uppercase">Primary mirror (default destination)</span>
              <div v-if="primaryMirrorName(redirector.group_mirror_id)" class="d-flex align-center mt-1">
                <v-icon small color="orange" class="mr-1">mdi-crown</v-icon>
                <span class="body-2 orange--text font-weight-medium">
                  {{ primaryMirrorName(redirector.group_mirror_id) }}
                </span>
              </div>
            </div>
          </div>

          <v-card class="ma-4" outlined>
            <v-expansion-panels
              v-model="redirectorNsPanel[redirector.redirector_id]"
              flat
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="expansion-panel-header px-4">
                  <div class="d-flex align-center">
                    <span class="body-1 black--text">Redirector Domain Name Servers</span>
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                      </template>
                      Name servers for this redirector domain
                    </v-tooltip>
                    <div class=" ml-6">
                      <div class="caption text-uppercase grey--text">NS Status</div>
                      <v-chip
                        small
                        outlined
                        :color="redirector.status === 'active' ? 'success' : 'grey'"
                        class="pa-2 text-capitalize"
                      >
                        {{ redirector.status }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="expansion-panel-content">
                  <v-divider />
                  <div class="px-4">
                    <template v-if="redirectorNsList(redirector).length">
                      <div
                        v-for="(ns, index) in redirectorNsList(redirector)"
                        :key="index"
                      >
                        <div class="d-flex align-center py-3">
                          <span class="body-1 font-weight-medium">NS{{ ns.settings?.priority ?? index + 1 }}:&nbsp;</span>
                          <span>{{ String(ns.name || '').toUpperCase() }}</span>
                          <v-btn
                            icon
                            x-small
                            class="ml-2"
                            @click.stop="copyToClipboard(ns.name)"
                          >
                            <v-icon small color="success">mdi-content-copy</v-icon>
                          </v-btn>
                        </div>
                        <v-divider v-if="index < redirectorNsList(redirector).length - 1" />
                      </div>
                    </template>
                    <div v-else class="redirectors-tab__empty-ns">
                      <div class="grey--text subtitle-1">
                        Loading NS from Cloudflare in progress
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>

          <v-card class="ma-4" outlined>
            <v-expansion-panels
              v-model="rulesPanel[redirector.redirector_id]"
              flat
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="expansion-panel-header px-4">
                  <div class="d-flex align-center">
                    <div class="d-flex align-center mr-6">
                      <span class="body-1 black--text">Redirector rules</span>
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                        </template>
                        Rules for redirecting traffic by mirror and geo
                      </v-tooltip>
                    </div>
                    <div class="d-flex align-center mt-2" @click.stop>
                      <v-tabs
                        :show-arrows="$vuetify.breakpoint.name === 'xs' || $vuetify.breakpoint.name === 'sm'"
                        :left="$vuetify.breakpoint.name !== 'xs' && $vuetify.breakpoint.name !== 'sm'"
                        v-model="rulesTab[redirector.redirector_id]"
                        class="redirectors-tab__rules-tabs flex-grow-1"
                      >
                        <v-tab class="mb-2">Group by Geo</v-tab>
                        <v-tab class="mb-2">Group by target</v-tab>
                      </v-tabs>
                    </div>

                    <v-spacer />
                    <template v-if="canEdit">
                      <v-btn
                        text
                        :disabled="savingRulesByRedirector === redirector.redirector_id"
                        class="ml-2"
                        @click.stop="cancelRedirectorRules(redirector)"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        class="ml-2"
                        :loading="savingRulesByRedirector === redirector.redirector_id"
                        @click.stop="saveRedirectorRules(redirector)"
                      >
                        <v-icon left small>mdi-content-save-outline</v-icon>
                        Save
                      </v-btn>
                    </template>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="expansion-panel-content">
                  <v-divider />
                  <div class="pa-0">
                    <v-card v-if="mirrorsInGroup(redirector).length" class="ma-4" outlined>
                      <div
                        v-if="rulesTab[redirector.redirector_id] === 0"
                        class="redirectors-tab__rules-by-geo"
                      >
                        <div class="d-flex align-center table-header px-4">
                          <span class="text-left text-uppercase caption" style="width: 20%">Geo</span>
                          <span class="text-left text-uppercase caption">Target</span>
                          <v-btn
                            v-if="canEdit"
                            text
                            color="success"
                            class="ml-auto"
                            small
                            @click="addGeoRuleRow(redirector)"
                          >
                            <v-icon left small>mdi-plus-circle-outline</v-icon>
                            Add rule
                          </v-btn>
                        </div>


                        <div class="mt-4 mb-4">
                          <div
                            v-for="(row, index) in (rulesByGeo[redirector.redirector_id] || [])"
                            :key="redirector.redirector_id + '-' + index"
                            class="d-flex align-end mb-2 px-4"
                          >
                            <div style="width: 20%">
                              <span class="caption grey--text">Visitor Geo:</span>
                              <select-countries
                                :chosen-countries="row.geo"
                                :countries="allCountries"
                                :multiple="false"
                                :outlined="false"
                                :disabled="!canEdit"
                                class="select-geo"
                                @input="(items) => updateGeoForRule(redirector, index, items)"
                              />
                            </div>

                            <div style="max-width: 220px" class="flex-grow-1 pa-0 mr-4">
                              <div class="caption grey--text">Target</div>
                              <v-select
                                :value="row.mirror_id"
                                :items="availableMirrorForGeo(redirector, row.geo)"
                                item-text="text"
                                item-value="value"
                                dense
                                hide-details
                                clearable
                                :disabled="!canEdit"
                                placeholder="Mirror"
                                @input="(val) => updateMirrorForRule(redirector, index, val)"
                              >
                                <template #selection="{ item }">
                                  <v-chip
                                    v-if="item"
                                    small
                                    outlined
                                    color="grey darken-3"
                                    close
                                    class="ma-0"
                                    @click:close="updateMirrorForRule(redirector, index, null)"
                                  >
                                    {{ item.text }}
                                  </v-chip>
                                </template>
                              </v-select>
                            </div>

                            <v-btn v-if="canEdit" icon color="error" @click="removeGeoRuleRow(redirector, index)">
                              <v-icon small>mdi-minus-circle-outline</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>

                      <v-simple-table v-else>
                        <thead class="table-header">
                          <tr>
                            <th class="text-left text-uppercase caption" style="width: 20%">Target</th>
                            <th class="text-left text-uppercase caption">Geo</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="mirror in mirrorsInGroup(redirector)"
                            :key="mirror.mirror_id"
                          >
                            <td class="body-2" style="width: 20%">{{ mirror.name }}</td>
                            <td class="d-flex align-center">
                              <select-countries
                                :chosen-countries="getGeoForMirror(redirector.redirector_id, mirror)"
                                :countries="availableCountriesForMirror(mirror)"
                                :outlined="false"
                                :chip="true"
                                :chips-code-display="true"
                                :disabled="!canEdit"
                                chip-color="#585858"
                                class="select-countries"
                                @input="(items) => setGeoToRules(redirector.redirector_id, mirror, items)"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card>
                    <div v-else class="pa-4">
                      <div class="grey--text subtitle-1 text-center">
                        No mirrors in the group
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <div v-if="hasProject && !loading && redirectors.length === 0" class="pa-6 grey--text text-center caption">
      No redirectors configured
    </div>
  </div>
</template>

<script>
  import { API } from '@/utils/api'
  import SelectCountries from '@/components/core/SelectCountries.vue'
  import { allCountries } from '@/components/core/SelectLang/mapperIsoLocale'

  export default {
    name: 'RedirectorsTab',
    components: {
      SelectCountries,
    },
    props: {
      project: {
        type: Number,
        default: null,
      },
      canEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        allCountries,
        loading: false,
        mirrors: [],
        groups: [],
        redirectors: [],
        redirectorsPanel: [],
        redirectorNsPanel: {},
        rulesPanel: {},
        rulesTab: {},
        rulesByGeo: {},
        savingRulesByRedirector: null,
      }
    },
    computed: {
      hasProject() {
        return this.project != null
      },
      groupSelectItems() {
        return this.groups.map((g) => ({ text: g.name, value: g.group_mirror_id }))
      },
    },
    watch: {
      project: {
        immediate: true,
        handler(entityId) {
          if (entityId) {
            this.loadRedirectors(entityId)
            return
          }
          this.mirrors = []
          this.groups = []
          this.redirectors = []
          this.redirectorsPanel = []
          this.redirectorNsPanel = {}
          this.rulesPanel = {}
          this.rulesTab = {}
          this.rulesByGeo = {}
          this.savingRulesByRedirector = null
        },
      },
    },
    methods: {
      expandAllRedirectorPanels() {
        this.redirectorsPanel = this.redirectors.map((_, index) => index)
      },
      expandRedirectorNsPanels() {
        this.redirectorNsPanel = Object.fromEntries(
          this.redirectors.map(r => [r.redirector_id, 0])
        )
      },

      syncRedirectorRulesState() {
        const geoRules = {}
        const tabs = {}
        const panels = {}

        this.redirectors.forEach((r) => {
          tabs[r.redirector_id] = 0
          panels[r.redirector_id] = 0
          geoRules[r.redirector_id] = this.formatRulesToGroupByGeo(r.rules || {}, r)
        })

        this.rulesByGeo = geoRules
        this.rulesTab = tabs
        this.rulesPanel = panels
      },

      formatRulesToGroupByGeo(rules, redirector) {
        return this.mirrorsInGroup(redirector).flatMap((m) => {
          const items =
            rules[m.mirror_id]?.items ??
            rules[String(m.mirror_id)]?.items ??
            []

          return items.map((geo) => ({
            geo,
            mirror_id: m.mirror_id,
          }))
        })
      },
      formatRulesToApiObject(redirectorId) {
        return (this.rulesByGeo[redirectorId] || []).reduce((acc, { geo, mirror_id }) => {
          if (!geo || !mirror_id) return acc
          const k = String(mirror_id)
          if (!acc[k]) acc[k] = { items: [] }
          if (!acc[k].items.includes(geo)) acc[k].items.push(geo)
          return acc
        }, {})
      },

      addGeoRuleRow(redirector) {
        const rid = redirector.redirector_id
        const rows = [...(this.rulesByGeo[rid] || [])]
        rows.push({ geo: null, mirror_id: null })
        this.$set(this.rulesByGeo, rid, rows)
      },
      removeGeoRuleRow(redirector, index) {
        const rid = redirector.redirector_id
        const rows = (this.rulesByGeo[rid] || []).filter((_, i) => i !== index)
        this.$set(this.rulesByGeo, rid, rows)
      },

      updateGeoForRule(redirector, index, items) {
        const geo = items?.value ?? null
        const rid = redirector.redirector_id

        const rows = [...(this.rulesByGeo[rid] || [])]
        const prev = rows[index] || {}

        const allowed = new Set(
          this.filterAvailableMirrorsForGeo(redirector, geo)
            .map(({ mirror_id }) => mirror_id)
        )

        const mirror_id = allowed.has(prev.mirror_id)
          ? prev.mirror_id
          : null

        rows[index] = {
          ...prev,
          geo,
          mirror_id,
        }

        this.$set(this.rulesByGeo, rid, rows)
      },
      updateMirrorForRule(redirector, index, val) {
        const rid = redirector.redirector_id
        const rows = [...(this.rulesByGeo[rid] || [])]

        if (!rows[index]) return

        rows[index] = {
          ...rows[index],
          mirror_id: val ?? null,
        }

        this.$set(this.rulesByGeo, rid, rows)
      },

      blockedOrRedirectGeoForMirror(mirror) {
        const geo = mirror?.settings?.geo_settings || {}
        const blocked = Array.isArray(geo.blocked_geo) ? geo.blocked_geo : []
        const redirect = Array.isArray(geo.redirect) ? geo.redirect : []

        return new Set([...blocked, ...redirect].map((code) => code))
      },
      filterAvailableMirrorsForGeo(redirector, geo) {
        return this.mirrorsInGroup(redirector).filter(
          (m) => !this.blockedOrRedirectGeoForMirror(m).has(geo),
        )
      },
      availableMirrorForGeo(redirector, geo) {
        return this.filterAvailableMirrorsForGeo(redirector, geo).map((m) => ({
          text: m.name,
          value: m.mirror_id,
        }))
      },

      mirrorsInGroup(redirector) {
        return this.mirrors.filter((m) => m.group_mirror_id === redirector.group_mirror_id)
      },

      getGeoForMirror(redirectorId, mirror) {
        return (this.rulesByGeo[redirectorId] || [])
          .filter((r) => r.mirror_id === mirror.mirror_id && r.geo)
          .map((r) => r.geo)
      },

      availableCountriesForMirror(mirror) {
        const excluded = this.blockedOrRedirectGeoForMirror(mirror)
        return this.allCountries.filter((c) => !excluded.has(String(c.value)))
      },

      setGeoToRules(redirectorId, mirror, selectedItems) {
        const rows = (this.rulesByGeo[redirectorId] || []).filter(
          (r) => r.mirror_id !== mirror.mirror_id,
        )
        const newRows = (selectedItems || []).map(({ value }) => ({
          geo: value,
          mirror_id: mirror.mirror_id,
        }))

        this.$set(this.rulesByGeo, redirectorId, [...rows, ...newRows])
      },

      cancelRedirectorRules(redirector) {
        this.$set(
          this.rulesByGeo,
          redirector.redirector_id,
          this.formatRulesToGroupByGeo(redirector.rules || {}, redirector),
        )
      },

      redirectorNsList(redirector) {
        return (redirector?.settings?.ns?.items || []).filter((ns) => ns?.enabled !== false)
      },
      copyToClipboard(text) {
        if (!text) return
        navigator.clipboard.writeText(text).then(() => {
          this.$showSuccess('Copied to clipboard')
        })
      },
      getGroup(groupMirrorId) {
        return this.groups.find(g => g.group_mirror_id === groupMirrorId) ?? null
      },

      getMirrorName(mirrorId) {
        return this.mirrors.find(x => x.mirror_id === mirrorId)?.name ?? null
      },

      primaryMirrorName(groupMirrorId) {
        const primaryId = this.getGroup(groupMirrorId)?.primary_mirror_id
        return this.getMirrorName(primaryId)
      },

      async saveRedirector(redirectorPayload) {
        if (!this.canEdit) return

        const redirector = { ...redirectorPayload }

        await API.call('/$v/mirrors-redirectors/redirector/update.json', {
          redirector,
        })
        await this.loadRedirectors(this.project)
        this.$showSuccess('Saved')
      },

      async saveRedirectorRules(redirector) {
        if (!this.canEdit) return

        this.savingRulesByRedirector = redirector.redirector_id
        try {
          const redirectorPayload = {
            entity_id: this.project,
            redirector_id: redirector.redirector_id,
            status: redirector.status,
            name: redirector.name,
            group_mirror_id: redirector.group_mirror_id,
            rules: this.formatRulesToApiObject(redirector.redirector_id),
          }
          await this.saveRedirector(redirectorPayload)
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to save redirector rules')
        } finally {
          this.savingRulesByRedirector = null
        }
      },
      async loadRedirectors(entityId) {
        this.loading = true
        try {
          const { data = {} } = await API.call('/$v/mirrors-redirectors/redirector/list.json', {
            entity_id: entityId ?? this.project,
          })
          this.mirrors = Array.isArray(data.mirrors) ? data.mirrors : []
          this.groups = Array.isArray(data.groups) ? data.groups : []
          this.redirectors = Array.isArray(data.redirectors) ? data.redirectors : []
          this.expandAllRedirectorPanels()
          this.expandRedirectorNsPanels()
          this.syncRedirectorRulesState()
          this.$emit('data-loaded', {
            mirrors: this.mirrors,
            groups: this.groups,
            redirectors: this.redirectors,
          })
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load redirectors')
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>
<style lang="scss" scoped>
  .redirectors-tab {

    &__empty-ns {
      min-height: 128px;
      display: flex;
      align-items: center;
      justify-content: center;
    }


    &__rules-tabs {
      min-width: 200px;
      max-width: 420px;
    }

    .table-header {
      background-color: #F0F0F0;
      color: #585858;
      height: 48px;
    }

    .expansion {
      display: flex;
      flex-direction: column;
      gap: 16px;

      &-panel {
        &-header {
          min-height: 56px;
          padding: 0 16px;

          &__col {
            min-height: 40px;
          }
        }

        &-content {
          ::v-deep .v-expansion-panel-content__wrap {
            padding: 0;
          }
        }
      }
    }
  }

  .select-countries {
    ::v-deep .custom-select {
      margin-left: 0;
      min-width: 330px;
      max-width: none;
    }
  }

  .select-geo {
    ::v-deep .custom-select {
      margin-left: 0;
      max-width: 90%;
    }
  }
</style>
